import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Dialog from '@radix-ui/react-dialog'
import {
  Plus, Radio, CalendarClock, Archive, FileText, Globe, MapPin, Bookmark,
  Search, MoreHorizontal, Trash2, Loader2, Copy, Pencil, AlertTriangle,
} from 'lucide-react'
import { toast } from 'sonner'
import { usePages, useDeletePage, useUpdatePage, useArchivePage } from '../api/hooks'
import type { Page, PageKind, PageBlockInput } from '../api/types'
import { ReschedulePageDialog } from '../components/page-editor/ReschedulePageDialog'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDatetime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function isPublishingSoon(scheduledFor: string) {
  return new Date(scheduledFor).getTime() - Date.now() < 24 * 60 * 60 * 1000
}

const KIND_LABEL: Record<PageKind, string> = {
  'front-page': 'Front Page',
  topic: 'Topic',
  region: 'Region',
  edition: 'Special Edition',
  book: 'Book',
}

const KIND_ICON: Record<PageKind, React.ElementType> = {
  'front-page': Globe,
  topic: FileText,
  region: MapPin,
  edition: Bookmark,
  book: FileText,
}

type KindFilter = 'all' | 'front-page' | 'topic' | 'region' | 'edition'

const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: 'all',        label: 'All' },
  { value: 'front-page', label: 'Front Page' },
  { value: 'topic',      label: 'Topic' },
  { value: 'region',     label: 'Region' },
  { value: 'edition',    label: 'Special Edition' },
]

// ─── Delete confirm dialog ────────────────────────────────────────────────────

function DeleteConfirmDialog({
  page, open, onOpenChange, onConfirm, isDeleting,
}: {
  page: Page | null; open: boolean; onOpenChange: (v: boolean) => void
  onConfirm: () => void; isDeleting: boolean
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded border border-line bg-canvas p-5 shadow-xl focus:outline-none"
          aria-describedby="delete-page-desc"
        >
          <Dialog.Title className="text-sm font-semibold text-fg">Delete page?</Dialog.Title>
          <Dialog.Description id="delete-page-desc" className="mt-2 text-xs text-fg-secondary">
            <span className="font-semibold text-fg">{page?.title}</span> will be permanently deleted.
          </Dialog.Description>
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button type="button" className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill">
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button" onClick={onConfirm} disabled={isDeleting}
              className="flex items-center gap-1.5 rounded bg-error-solid px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-error-solid-hover disabled:opacity-50"
            >
              {isDeleting && <Loader2 size={11} className="animate-spin" aria-hidden="true" />}
              Delete
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ─── Page card ────────────────────────────────────────────────────────────────

function PageCard({ page, onDelete, onReschedule, onArchive }: { page: Page; onDelete: (p: Page) => void; onReschedule: (p: Page) => void; onArchive: (p: Page) => void }) {
  const Icon = KIND_ICON[page.kind]
  const isLive = page.status === 'live'
  const isScheduled = page.status === 'scheduled'
  const isDraft = page.status === 'draft'
  const soon = isScheduled && page.scheduledFor ? isPublishingSoon(page.scheduledFor) : false

  return (
    <div
      className={`group relative flex flex-col rounded-lg border bg-canvas transition-shadow hover:shadow-md ${
        isLive      ? 'border-success-line' :
        soon        ? 'border-warning-line' :
        isScheduled ? 'border-accent-primary-solid/40' :
                      'border-line'
      }`}
    >
      {/* Status bar */}
      <div className={`flex items-center justify-between rounded-t-lg px-3 py-1.5 ${
        isLive      ? 'bg-success-fill'  :
        soon        ? 'bg-warning-fill'  :
        isScheduled ? 'bg-accent-primary-fill' :
        isDraft     ? 'bg-fill'     :
                      'bg-fill opacity-60'
      }`}>
        <div className="flex items-center gap-1.5">
          {isLive && (
            <>
              <span className="h-1.5 w-1.5 rounded-full bg-success-solid animate-pulse" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-success-fg">Live</span>
            </>
          )}
          {isScheduled && !soon && (
            <>
              <CalendarClock size={10} className="text-accent-primary-fg" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-accent-primary-fg">Scheduled</span>
              {page.scheduledFor && (
                <span className="text-[10px] text-accent-primary-fg opacity-70">&mdash; {formatDatetime(page.scheduledFor)}</span>
              )}
            </>
          )}
          {isScheduled && soon && (
            <>
              <AlertTriangle size={10} className="text-warning-fg" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-warning-fg">Publishing Soon</span>
              {page.scheduledFor && (
                <span className="text-[10px] text-warning-fg opacity-70">&mdash; {formatDatetime(page.scheduledFor)}</span>
              )}
            </>
          )}
          {isDraft && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Draft</span>
          )}
          {page.status === 'archived' && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Archived</span>
          )}
        </div>
        {/* Kind pill */}
        <div className="flex items-center gap-1 text-[10px] text-fg-secondary">
          <Icon size={10} aria-hidden="true" />
          <span>{KIND_LABEL[page.kind]}</span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="min-w-0">
          <Link
            to={`/admin/pages/${page.id}`}
            className="block truncate text-sm font-semibold text-fg leading-snug hover:underline"
          >
            {page.title}
          </Link>
          <p className="mt-0.5 font-mono text-[10px] text-fg-secondary opacity-60">/{page.slug}</p>
        </div>

        {/* Timestamp line */}
        <p className="text-[11px] text-fg-secondary">
          {isLive && page.publishedAt && `Live since ${formatDatetime(page.publishedAt)}`}
          {isScheduled && page.scheduledFor && (
            <span className={`flex items-center gap-1 ${soon ? 'font-semibold text-warning-fg' : ''}`}>
              <CalendarClock size={10} aria-hidden="true" />
              {formatDatetime(page.scheduledFor)}
            </span>
          )}
          {isDraft && `Edited ${formatDate(page.updatedAt)}`}
          {page.status === 'archived' && page.archivedAt && `Archived ${formatDate(page.archivedAt)}`}
        </p>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between border-t border-line px-3 py-2">
        <Link
          to={`/admin/pages/${page.id}`}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
        >
          <Pencil size={11} aria-hidden="true" />
          {isLive ? 'View' : 'Edit'}
        </Link>

        <div className="flex items-center gap-1">
          {/* Reschedule — scheduled pages only */}
          {isScheduled && (
            <button
              type="button"
              onClick={() => onReschedule(page)}
              className="flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
              title="Change publish time"
            >
              <CalendarClock size={11} aria-hidden="true" />
              Reschedule
            </button>
          )}

          {/* Clone — always available */}
          <Link
            to={`/admin/pages/new?cloneFrom=${page.id}`}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            title="Clone this page into a new draft"
          >
            <Copy size={11} aria-hidden="true" />
            Clone
          </Link>

          {/* Delete overflow menu */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label={`More actions for ${page.title}`}
                className="rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
              >
                <MoreHorizontal size={13} aria-hidden="true" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end" sideOffset={4} className="z-50 min-w-[150px] rounded border border-line bg-surface shadow-lg">
                {isLive && (page.kind === 'edition' || ((page.kind === 'topic' || page.kind === 'region') && page.slug !== 'default')) && (
                  <DropdownMenu.Item
                    onSelect={() => onArchive(page)}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-fg outline-none transition-colors data-[highlighted]:bg-fill"
                  >
                    <Archive size={12} aria-hidden="true" />
                    Archive
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  onSelect={() => onDelete(page)}
                  disabled={isLive}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-error-fg outline-none transition-colors data-[highlighted]:bg-fill data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
                >
                  <Trash2 size={12} aria-hidden="true" />
                  {isLive ? 'Cannot delete live page' : 'Delete'}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </div>
  )
}

// ─── Lane ─────────────────────────────────────────────────────────────────────

function Lane({
  title, icon: Icon, pages, emptyText, accent, onDelete, onReschedule, onArchive,
}: {
  title: string; icon: React.ElementType; pages: Page[]
  emptyText: string; accent: string; onDelete: (p: Page) => void; onReschedule: (p: Page) => void; onArchive: (p: Page) => void
}) {
  return (
    <div className="flex w-80 shrink-0 flex-col">
      {/* Lane header */}
      <div className={`mb-3 flex items-center gap-2 border-b-2 pb-2 ${accent}`}>
        <Icon size={12} aria-hidden="true" />
        <span className="text-[11px] font-bold uppercase tracking-widest">{title}</span>
        <span className="ml-auto rounded-full bg-fill px-2 py-0.5 text-[10px] font-semibold text-fg-secondary">
          {pages.length}
        </span>
      </div>

      {/* Cards */}
      {pages.length === 0 ? (
        <p className="mt-2 text-center text-xs italic text-fg-secondary opacity-50">{emptyText}</p>
      ) : (
        <div className="space-y-3">
          {pages.map((p) => <PageCard key={p.id} page={p} onDelete={onDelete} onReschedule={onReschedule} onArchive={onArchive} />)}
        </div>
      )}
    </div>
  )
}

// ─── Archived row (compact list) ──────────────────────────────────────────────

function ArchivedRow({ page, onDelete }: { page: Page; onDelete: (p: Page) => void }) {
  const Icon = KIND_ICON[page.kind]
  return (
    <li className="flex items-center gap-3 rounded px-3 py-2.5 transition-colors hover:bg-fill">
      <Icon size={12} className="shrink-0 text-fg-secondary opacity-50" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-fg opacity-60">{page.title}</p>
        <p className="font-mono text-[10px] text-fg-secondary opacity-40">/{page.slug}</p>
      </div>
      <span className="shrink-0 text-[10px] text-fg-secondary opacity-40">
        {page.archivedAt ? formatDate(page.archivedAt) : '—'}
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <Link
          to={`/admin/pages/new?cloneFrom=${page.id}`}
          className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          title="Clone into a new draft"
        >
          <Copy size={10} aria-hidden="true" />
          Clone
        </Link>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" aria-label={`Actions for ${page.title}`} className="rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg transition-colors">
              <MoreHorizontal size={12} aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="end" sideOffset={4} className="z-50 min-w-[140px] rounded border border-line bg-surface shadow-lg">
              <DropdownMenu.Item
                onSelect={() => onDelete(page)}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-error-fg outline-none data-[highlighted]:bg-fill"
              >
                <Trash2 size={12} aria-hidden="true" />
                Delete
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </li>
  )
}

// ─── PageListPage ──────────────────────────────────────────────────────────────

function PageListPage() {
  const { data: pages, isLoading, isError } = usePages()
  const { mutate: deletePage, isPending: isDeleting } = useDeletePage()
  const { mutate: archivePage } = useArchivePage()
  const [kindFilter, setKindFilter] = useState<KindFilter>('all')
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Page | null>(null)
  const [rescheduleTarget, setRescheduleTarget] = useState<Page | null>(null)
  const { mutate: updatePage, isPending: isRescheduling } = useUpdatePage(rescheduleTarget?.id ?? '')

  const activePages = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (pages ?? [])
      .filter((p) => p.status !== 'archived')
      .filter((p) => p.kind !== 'book')
      .filter((p) => kindFilter === 'all' || p.kind === kindFilter)
      .filter((p) => !q || p.title.toLowerCase().includes(q))
  }, [pages, kindFilter, search])

  const archivedPages = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (pages ?? [])
      .filter((p) => p.status === 'archived')
      .filter((p) => p.kind !== 'book')
      .filter((p) => kindFilter === 'all' || p.kind === kindFilter)
      .filter((p) => !q || p.title.toLowerCase().includes(q))
  }, [pages, kindFilter, search])

  const livePages = activePages.filter((p) => p.status === 'live')
  const scheduledPages = activePages
    .filter((p) => p.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduledFor!).getTime() - new Date(b.scheduledFor!).getTime())
  const draftPages = activePages.filter((p) => p.status === 'draft')

  // Alarms: warn if no live default topic or region page exists.
  // These are the fallback layouts for all topic/region section URLs — missing
  // one means those reader URLs silently fail.
  const hasLiveDefaultTopic = (pages ?? []).some(
    (p) => p.kind === 'topic' && p.slug === 'default' && p.status === 'live',
  )
  const hasLiveDefaultRegion = (pages ?? []).some(
    (p) => p.kind === 'region' && p.slug === 'default' && p.status === 'live',
  )
  const draftDefaultTopic = (pages ?? []).find(
    (p) => p.kind === 'topic' && p.slug === 'default' && p.status !== 'live' && p.status !== 'archived',
  )
  const draftDefaultRegion = (pages ?? []).find(
    (p) => p.kind === 'region' && p.slug === 'default' && p.status !== 'live' && p.status !== 'archived',
  )

  function handleDeleteConfirm() {
    if (!deleteTarget) return
    deletePage(deleteTarget.id, {
      onSuccess: () => { toast.success('Page deleted'); setDeleteTarget(null) },
      onError: () => toast.error('Failed to delete page.'),
    })
  }

  function handleArchive(page: Page) {
    archivePage(page.id, {
      onSuccess: () => toast.success(`"${page.title}" archived`),
      onError: () => toast.error('Failed to archive page.'),
    })
  }

  function handleRescheduleConfirm(scheduledFor: string) {
    if (!rescheduleTarget) return
    updatePage(
      {
        kind: rescheduleTarget.kind,
        slug: rescheduleTarget.slug,
        title: rescheduleTarget.title,
        blocks: rescheduleTarget.blocks as unknown as PageBlockInput[],
        originLayoutId: rescheduleTarget.originLayoutId,
        scheduledFor,
      },
      {
        onSuccess: () => { toast.success('Page rescheduled'); setRescheduleTarget(null) },
        onError: () => toast.error('Failed to reschedule page.'),
      },
    )
  }

  const soonCount = scheduledPages.filter((p) => p.scheduledFor && isPublishingSoon(p.scheduledFor)).length

  return (
    <div className="relative min-h-screen">

      {/* Hero gradient wash */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-56"
        style={{ background: 'linear-gradient(to bottom, var(--violet-4), var(--violet-2), transparent)', opacity: 0.5 }}
      />

      {/* Header */}
      <div className="relative px-6 pt-5">
        <div className="flex items-center justify-between border-b border-line pb-4">
          {/* Left: title + kind filter pills */}
          <div className="flex items-center gap-8">
            <h1 className="font-zodiak pl-8 text-3xl font-bold text-fg">Production Desk</h1>

            {/* Kind filter pills */}
            <div className="flex items-center gap-2">
              {KIND_FILTERS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setKindFilter(value)}
                  className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                    kindFilter === value
                      ? 'border-fg bg-fg text-canvas'
                      : 'border-line bg-canvas text-fg-secondary hover:border-line-strong hover:text-fg'
                  }`}
                >
                  {label}
                </button>
              ))}
              {soonCount > 0 && (
                <span className="flex items-center gap-1.5 rounded-full border border-warning-line bg-warning-fill px-3 py-1 text-[11px] font-semibold text-warning-fg">
                  <AlertTriangle size={10} aria-hidden="true" />
                  {soonCount} publishing within 24h
                </span>
              )}
            </div>
          </div>

          {/* Right: search + new page */}
          <div className="flex items-center gap-3">
            <div className="relative flex items-center">
              <Search size={12} className="pointer-events-none absolute left-2.5 text-fg-secondary" aria-hidden="true" />
              <input
                type="search"
                placeholder="Filter by title…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-44 rounded border border-line bg-surface pl-7 pr-3 text-xs text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
              />
            </div>
            <Link
              to="/admin/pages/new"
              className="flex h-8 items-center gap-2 rounded border border-line bg-surface px-3 text-xs font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill"
            >
              <Plus size={13} aria-hidden="true" />
              New Page
            </Link>
          </div>
        </div>
      </div>

      <div className="relative px-6 py-6">

        {/* Loading */}
        {isLoading && (
          <div className="mt-24 flex justify-center">
            <Loader2 size={20} className="animate-spin text-fg-secondary" />
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="mt-16 rounded border border-error-line bg-error-fill px-5 py-4 text-sm text-error-fg">
            Failed to load pages. Please refresh.
          </div>
        )}

        {/* Three-lane board */}
        {!isLoading && !isError && (
          <>
            {/* Default page alarms */}
            {(!hasLiveDefaultTopic || !hasLiveDefaultRegion) && (
              <div className="mb-6 space-y-2">
                {!hasLiveDefaultTopic && (
                  <div className="flex items-start gap-3 rounded border border-error-line bg-error-fill px-4 py-3">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-error-fg" aria-hidden="true" />
                    <div className="flex-1 text-sm text-error-fg">
                      <span className="font-semibold">No live default topic page.</span>
                      {' '}Topic section pages won't render for readers without a live default layout.{' '}
                      {draftDefaultTopic ? (
                        <Link to={`/admin/pages/${draftDefaultTopic.id}`} className="font-semibold underline hover:no-underline">
                          Open and publish it →
                        </Link>
                      ) : (
                        <Link to="/admin/pages/new" className="font-semibold underline hover:no-underline">
                          Create one now →
                        </Link>
                      )}
                    </div>
                  </div>
                )}
                {!hasLiveDefaultRegion && (
                  <div className="flex items-start gap-3 rounded border border-error-line bg-error-fill px-4 py-3">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-error-fg" aria-hidden="true" />
                    <div className="flex-1 text-sm text-error-fg">
                      <span className="font-semibold">No live default region page.</span>
                      {' '}Region section pages won't render for readers without a live default layout.{' '}
                      {draftDefaultRegion ? (
                        <Link to={`/admin/pages/${draftDefaultRegion.id}`} className="font-semibold underline hover:no-underline">
                          Open and publish it →
                        </Link>
                      ) : (
                        <Link to="/admin/pages/new" className="font-semibold underline hover:no-underline">
                          Create one now →
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-center gap-4 overflow-x-auto pb-6">
              <Lane
                title="Drafts"
                icon={FileText}
                pages={draftPages}
                emptyText="No drafts in progress"
                accent="border-line text-fg-secondary"
                onDelete={setDeleteTarget}
                onReschedule={setRescheduleTarget}
                onArchive={handleArchive}
              />
              <Lane
                title="Scheduled"
                icon={CalendarClock}
                pages={scheduledPages}
                emptyText="Nothing scheduled"
                accent="border-accent-primary-solid text-accent-primary-fg"
                onDelete={setDeleteTarget}
                onReschedule={setRescheduleTarget}
                onArchive={handleArchive}
              />
              <Lane
                title="Live"
                icon={Radio}
                pages={livePages}
                emptyText="No live pages"
                accent="border-success-line text-success-fg"
                onDelete={setDeleteTarget}
                onReschedule={setRescheduleTarget}
                onArchive={handleArchive}
              />
            </div>

            {/* Archived accordion */}
            <div className="mx-auto mt-10 w-full max-w-[1008px] border-t border-line pt-6">
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-fg-secondary transition-colors hover:text-fg"
              >
                <Archive size={11} aria-hidden="true" />
                Archived
                <span className="rounded-full bg-fill px-2 py-0.5 text-[10px] font-semibold">{archivedPages.length}</span>
                <span className="ml-1 opacity-40">{showArchived ? '▲' : '▼'}</span>
              </button>

              {showArchived && (
                <div className="mt-4">
                  {archivedPages.length === 0 ? (
                    <p className="text-xs italic text-fg-secondary opacity-50">No archived pages.</p>
                  ) : (
                    <ul className="divide-y divide-line rounded-lg border border-line">
                      {archivedPages.map((p) => (
                        <ArchivedRow key={p.id} page={p} onDelete={setDeleteTarget} />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <DeleteConfirmDialog
        page={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      <ReschedulePageDialog
        open={rescheduleTarget !== null}
        onOpenChange={(open) => { if (!open) setRescheduleTarget(null) }}
        onConfirm={handleRescheduleConfirm}
        isPending={isRescheduling}
        pageTitle={rescheduleTarget?.title}
        currentScheduledFor={rescheduleTarget?.scheduledFor}
      />
    </div>
  )
}

export default PageListPage
